import React, { Suspense, useMemo } from "react";
import Container from "react-bootstrap/Container";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Accordion from "react-bootstrap/Accordion";

import "./ClassOverview.css";
import "./SencoResultsClassOverview.css";

import SencoResultsStatus from "./SencoResultsStatus";
import SimpleNavigationButton from "./SimpleNavigationButton";

function SencoResultsClassOverview(props) {
  // Destructure props for clarity
  const { 
    gradeLevel, 
    teachers, 
    teacherid, 
    schoolid, 
    classIds, 
    name, 
    academicYear,
    allClassesAssessments,
    gradeLevel1Assessments,
    gradeLevel2Assessments,
    gradeLevel3Assessments
  } = props;

  // Log the assessments for debugging
  console.log("Grade Level 1 Assessments:", gradeLevel1Assessments);
  console.log("Grade Level 2 Assessments:", gradeLevel2Assessments);
  console.log("Grade Level 3 Assessments:", gradeLevel3Assessments);

  const firstPrefix = useMemo(() => {
    // Find a proper senior assessment name (grade_level2 = true)
    if (!gradeLevel3Assessments || !Array.isArray(gradeLevel3Assessments) || gradeLevel3Assessments.length === 0) {
      return "1st"; // Default fallback
    }

    // Look through all class assessments
    for (const classAssessment of gradeLevel3Assessments) {
      if (!classAssessment?.assessments) continue;
      
      // Look for assessments with grade_level2 = true and a name containing "Senior"
      const firstAssessments = Object.values(classAssessment.assessments)
        .filter(a => a && a.grade_level3 === true && a.name && a.name.includes("1st"));
      
      if (firstAssessments.length > 0) {
        const name = firstAssessments[0].name.split(" ")[0];
        return name || "1st";
      }
    }
    
    // If no specific Senior assessment found, look for any grade_level2 assessment
    for (const classAssessment of gradeLevel3Assessments) {
      if (!classAssessment?.assessments) continue;
      
      const level3Assessments = Object.values(classAssessment.assessments)
        .filter(a => a && a.grade_level3 === true && a.name);
      
      if (level3Assessments.length > 0) {
        const name = level3Assessments[0].name.split(" ")[0];
        return name || "1st";
      }
    }
    
    return "1st"; // Default if no appropriate assessment found
  }, [gradeLevel3Assessments]);

  // Get proper senior prefix - only consider grade_level2 assessments
  const snrPrefix = useMemo(() => {
    // Find a proper senior assessment name (grade_level2 = true)
    if (!gradeLevel2Assessments || !Array.isArray(gradeLevel2Assessments) || gradeLevel2Assessments.length === 0) {
      return "Senior"; // Default fallback
    }

    // Look through all class assessments
    for (const classAssessment of gradeLevel2Assessments) {
      if (!classAssessment?.assessments) continue;
      
      // Look for assessments with grade_level2 = true and a name containing "Senior"
      const seniorAssessments = Object.values(classAssessment.assessments)
        .filter(a => a && a.grade_level2 === true && a.name && a.name.includes("Senior"));
      
      if (seniorAssessments.length > 0) {
        const name = seniorAssessments[0].name.split(" ")[0];
        return name || "Senior";
      }
    }
    
    // If no specific Senior assessment found, look for any grade_level2 assessment
    for (const classAssessment of gradeLevel2Assessments) {
      if (!classAssessment?.assessments) continue;
      
      const level2Assessments = Object.values(classAssessment.assessments)
        .filter(a => a && a.grade_level2 === true && a.name);
      
      if (level2Assessments.length > 0) {
        const name = level2Assessments[0].name.split(" ")[0];
        return name || "Senior";
      }
    }
    
    return "Senior"; // Default if no appropriate assessment found
  }, [gradeLevel2Assessments]);

  // Get proper junior prefix - only consider grade_level1 assessments
  const jnrPrefix = useMemo(() => {
    // Find a proper junior assessment name (grade_level1 = true)
    if (!gradeLevel1Assessments || !Array.isArray(gradeLevel1Assessments) || gradeLevel1Assessments.length === 0) {
      return "Junior"; // Default fallback
    }

    // Look through all class assessments
    for (const classAssessment of gradeLevel1Assessments) {
      if (!classAssessment?.assessments) continue;
      
      // Look for assessments with grade_level1 = true and a name containing "Junior"
      const juniorAssessments = Object.values(classAssessment.assessments)
        .filter(a => a && a.grade_level1 === true && a.name && a.name.includes("Junior"));
      
      if (juniorAssessments.length > 0) {
        const name = juniorAssessments[0].name.split(" ")[0];
        return name || "Junior";
      }
    }
    
    // If no specific Junior assessment found, look for any grade_level1 assessment
    for (const classAssessment of gradeLevel1Assessments) {
      if (!classAssessment?.assessments) continue;
      
      const level1Assessments = Object.values(classAssessment.assessments)
        .filter(a => a && a.grade_level1 === true && a.name);
      
      if (level1Assessments.length > 0) {
        const name = level1Assessments[0].name.split(" ")[0];
        return name || "Junior";
      }
    }
    
    return "Junior"; // Default if no appropriate assessment found
  }, [gradeLevel1Assessments]);

  return (
    <div>
      <Row className="inline-block align-items-center push-down">
        <Col className="col-auto">
          <Suspense fallback={<div>Loading Navigation Button...</div>}>
            <SimpleNavigationButton
              path="/masterdashboard"
              label="Back"
              colour="blue"
            />
          </Suspense>
        </Col>
        <Col className="flex-grow-1 text-center" style={{ margin: "10px" }}>
          <h2 className="page-description-header-results">Results Overview</h2>
        </Col>
      </Row>
      <div className="class-overview-container">
        <Container className="container-shape-senco">
          <Row className="page-description-bar-senco">
            <Col className="col-7">
              <Row className="inline-block">
                <Col className="col-6">
                  <h2 className="page-description-header">Results</h2>
                </Col>
              </Row>
            </Col>
          </Row>
          <hr />
          <Row>
            <Col>
            {gradeLevel3Assessments && gradeLevel3Assessments.length > 0 && (
                <Suspense fallback={<div>Loading Results Status...</div>}>
                  <Accordion
                    className="results-custom-accordion"
                    defaultActiveKey="0"
                  >
                    <Accordion.Item eventKey="0">
                      <Accordion.Header>
                        <strong>{firstPrefix} Screener Results</strong>
                      </Accordion.Header>
                      <Accordion.Body>
                        <Row>
                          <Col>
                            <SencoResultsStatus
                              allClassesAssessments={gradeLevel3Assessments}
                              teachers={teachers}
                              schoolid={schoolid}
                              classIds={classIds}
                              name={name}
                              teacherid={teacherid}
                              academicYear={academicYear}
                              gradeLevel={3} // Explicitly indicate grade level
                            />
                          </Col>
                          <Col className="col-auto">
                            {/* Report button can be added back here if needed */}
                          </Col>
                        </Row>
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                </Suspense>
              )}
              {/* Displaying SencoResultsStatus for grade level 2 (Senior) */}
              {gradeLevel2Assessments && gradeLevel2Assessments.length > 0 && (
                <Suspense fallback={<div>Loading Results Status...</div>}>
                  <Accordion
                    className="results-custom-accordion"
                    defaultActiveKey="0"
                  >
                    <Accordion.Item eventKey="0">
                      <Accordion.Header>
                        <strong>{snrPrefix} Screener Results</strong>
                      </Accordion.Header>
                      <Accordion.Body>
                        <Row>
                          <Col>
                            <SencoResultsStatus
                              allClassesAssessments={gradeLevel2Assessments}
                              teachers={teachers}
                              schoolid={schoolid}
                              classIds={classIds}
                              name={name}
                              teacherid={teacherid}
                              academicYear={academicYear}
                              gradeLevel={2} // Explicitly indicate grade level
                            />
                          </Col>
                          <Col className="col-auto">
                            {/* Report button can be added back here if needed */}
                          </Col>
                        </Row>
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                </Suspense>
              )}
              {/* Displaying SencoResultsStatus for grade level 1 (Junior) */}
              {gradeLevel1Assessments && gradeLevel1Assessments.length > 0 && (
                <Suspense fallback={<div>Loading Results Status...</div>}>
                  <Accordion className="results-custom-accordion">
                    <Accordion.Item eventKey="1">
                      <Accordion.Header>
                        <strong>{jnrPrefix} Screener Results</strong>
                      </Accordion.Header>
                      <Accordion.Body>
                        <Row>
                          <Col>
                            <SencoResultsStatus
                              allClassesAssessments={gradeLevel1Assessments}
                              teachers={teachers}
                              schoolid={schoolid}
                              classIds={classIds}
                              name={name}
                              teacherid={teacherid}
                              academicYear={academicYear}
                              gradeLevel={1} // Explicitly indicate grade level
                            />
                          </Col>
                          <Col className="col-auto">
                            {/* Report button can be added back here if needed */}
                          </Col>
                        </Row>
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                </Suspense>
              )}
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
}

export default SencoResultsClassOverview;

// import React, { useEffect, useState, Suspense, useMemo } from "react";
// import Container from "react-bootstrap/Container";
// import Col from "react-bootstrap/Col";
// import Row from "react-bootstrap/Row";
// import Accordion from "react-bootstrap/Accordion";

// import "./ClassOverview.css";
// import "./SencoResultsClassOverview.css";

// import SencoResultsStatus from "./SencoResultsStatus"; // Import the new SencoResultsStatus component
// // import ReportsButton from "./ReportsButton";
// import SimpleNavigationButton from "./SimpleNavigationButton";
// // import GradeCard from "./GradeCard";

// function SencoResultsClassOverview(props) {
//   let gradeLevel = props.gradeLevel;
//   let teachers = props.teachers;
//   let teacherid = props.teacherid;
//   let schoolid = props.schoolid;
//   let classIds = props.classIds;
//   let name = props.name;
//   let selectedYear = props.selectedYear;
//   let YEAR_RANGES = props.YEAR_RANGES;

//   console.log(props);

//   const [allClassesAssessments, setAllClassesAssessments] = useState([]);
//   const { gradeLevel1Assessments, gradeLevel2Assessments } = props;

//   // Assuming you fetch the aggregated class assessments from the props or from an API call
//   useEffect(() => {
//     if (props.allClassesAssessments) {
//       setAllClassesAssessments(props.allClassesAssessments);
//     }
//   }, [props.allClassesAssessments]);

//   console.log(gradeLevel1Assessments, "gradeLevel1Assessments");

//   const jnrPrefix = useMemo(() => {
//     const firstAssessment = Object.values(
//       gradeLevel1Assessments[0]?.assessments || {}
//     )[0];
//     return firstAssessment?.name.split(" ")[0];
//   }, [gradeLevel1Assessments]);

//   const snrPrefix = useMemo(() => {
//     const firstAssessment = Object.values(
//       gradeLevel2Assessments[0]?.assessments || {}
//     )[0];
//     return firstAssessment?.name.split(" ")[0];
//   }, [gradeLevel2Assessments]);

//   return (
//     <div>
//       <Row className="inline-block align-items-center push-down">
//         <Col className="col-auto">
//           <Suspense fallback={<div>Loading Navigation Button...</div>}>
//             <SimpleNavigationButton
//               path="/masterdashboard"
//               label="Back"
//               colour="blue"
//             />
//           </Suspense>
//         </Col>
//         <Col className="flex-grow-1 text-center" style={{ margin: "10px" }}>
//           <h2 className="page-description-header-results">Results Overview</h2>
//         </Col>
//       </Row>
//       <div className="class-overview-container">
//         <Container className="container-shape-senco">
//           <Row className="page-description-bar-senco">
//             <Col className="col-7">
//               <Row className="inline-block">
//                 <Col className="col-6">
//                   {/* <h2 className="page-description-header">
//                     SENCO Results Overview
//                   </h2> */}
//                   <h2 className="page-description-header">Results</h2>
//                 </Col>
//                 {/* <Col className="col-3">
//                   <Suspense fallback={<div>Loading Grade Card...</div>}>
//                     <GradeCard
//                       gradeLevel={gradeLevel}
//                       gradeLevel1={props.grade_level1}
//                       gradeLevel2={props.grade_level2}
//                       trialClass={props.trial_class}
//                     />
//                   </Suspense>
//                 </Col> */}
//               </Row>
//               {/* <p className="paragraph-font-size">
//                 Select the assessment that you would like to see the results
//                 for.
//               </p> */}
//             </Col>
//           </Row>
//           <hr />
//           <Row>
//             <Col>
//               {/* Displaying SencoResultsStatus for grade level 2 */}
//               {gradeLevel2Assessments.length > 0 && (
//                 <Suspense fallback={<div>Loading Results Status...</div>}>
//                   <Accordion
//                     className="results-custom-accordion"
//                     defaultActiveKey="0"
//                   >
//                     <Accordion.Item eventKey="0">
//                       <Accordion.Header>
//                         <strong>{snrPrefix} Screener Results</strong>
//                       </Accordion.Header>
//                       <Accordion.Body>
//                         <Row>
//                           <Col>
//                             <SencoResultsStatus
//                               allClassesAssessments={gradeLevel2Assessments}
//                               teachers={teachers}
//                               schoolid={schoolid}
//                               classIds={classIds}
//                               name={name}
//                               teacherid={teacherid}
//                               YEAR_RANGES={YEAR_RANGES}
//                               selectedYear={selectedYear}
//                             />
//                           </Col>
//                           <Col className="col-auto">
//                             {/* <ReportsButton
//                               classAssessmentsValues={gradeLevel2Assessments}
//                               classDetails={props.classDetails}
//                               teachers={teachers}
//                               teacherid={teacherid}
//                               schoolid={schoolid}
//                             /> */}
//                           </Col>
//                         </Row>
//                       </Accordion.Body>
//                     </Accordion.Item>
//                   </Accordion>
//                 </Suspense>
//               )}
//               {/* Displaying SencoResultsStatus for grade level 1 */}
//               {gradeLevel1Assessments.length > 0 && (
//                 <Suspense fallback={<div>Loading Results Status...</div>}>
//                   <Accordion className="results-custom-accordion">
//                     <Accordion.Item eventKey="1">
//                       <Accordion.Header>
//                         <strong>{jnrPrefix} Screener Results</strong>
//                       </Accordion.Header>
//                       <Accordion.Body>
//                         <Row>
//                           <Col>
//                             <SencoResultsStatus
//                               allClassesAssessments={gradeLevel1Assessments}
//                               teachers={teachers}
//                               schoolid={schoolid}
//                               classIds={classIds}
//                               name={name}
//                               teacherid={teacherid}
//                               YEAR_RANGES={YEAR_RANGES}
//                               selectedYear={selectedYear}
//                             />
//                           </Col>
//                           <Col className="col-auto">
//                             {/* <ReportsButton
//                               classAssessmentsValues={gradeLevel1Assessments}
//                               classDetails={props.classDetails}
//                               teachers={teachers}
//                               teacherid={teacherid}
//                               schoolid={schoolid}
//                             /> */}
//                           </Col>
//                         </Row>
//                       </Accordion.Body>
//                     </Accordion.Item>
//                   </Accordion>
//                 </Suspense>
//               )}
//               {/* Optional Back Button */}
//               {/* <Col>
//                 <Suspense fallback={<div>Loading Navigation Button...</div>}>
//                   <SimpleNavigationButton
//                     path="/resultsdashboard"
//                     label="Back"
//                     colour="blue"
//                   />
//                 </Suspense>
//               </Col> */}
//             </Col>
//           </Row>
//         </Container>
//       </div>
//     </div>
//   );
// }

// export default SencoResultsClassOverview;
